import React from "react";
import { Camera, CameraOff, RefreshCw, SwitchCamera } from "lucide-react";
import { TOUCH_FEEDBACK_STYLE } from "../../../../constants/secondaryCamera";
import CameraActionButton from "../CameraActionButton";
import RotateDeviceOverlay from "../RotateDeviceOverlay";
import SetupProgress from "./SetupProgress";
import WizardBackButton from "./WizardBackButton";
import WizardPrimaryButton from "./WizardPrimaryButton";

// Live self-view (PHASES.POSITIONING) — phone's own camera, local only; not the parked WebRTC Phase 2 preview.
const PositioningSlide = ({
  candidateName,
  jobRole,
  videoRefCallback,
  cameraArmed,
  connecting,
  onRetryCamera,
  onSwitchCamera,
  showBack,
  onBack,
  onConfirm,
  confirmLoading,
  error,
  permissionDenied,
  progressCurrent,
  progressTotal,
  isLandscape,
}) => {
  return (
    <div className="flex flex-col bg-gray-900 text-white h-screen" style={{ height: "100dvh" }}>
      <div className="px-5 pb-2" style={{ paddingTop: "calc(1rem + env(safe-area-inset-top))" }}>
        <div className="mb-3">
          <SetupProgress current={progressCurrent} total={progressTotal} />
        </div>
        <p className="text-xs text-gray-400 text-center truncate">
          {candidateName} {jobRole ? `· ${jobRole}` : ""}
        </p>
      </div>

      <div className="flex-1 px-5 pb-2 min-h-0">
        <div className="relative h-full bg-gray-800/50 backdrop-blur-sm border border-gray-700/50 rounded-xl overflow-hidden">
          {!isLandscape && <RotateDeviceOverlay />}
          {cameraArmed ? (
            <>
              <video ref={videoRefCallback} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute top-3 left-3 right-3 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2">
                <p className="text-xs text-gray-100 text-center leading-snug">
                  Position your phone so it can see you, your laptop screen, and the desk
                </p>
              </div>
              {!confirmLoading && (
                <div className="absolute bottom-3 right-3">
                  <CameraActionButton onClick={onSwitchCamera} title="Switch Camera" icon={SwitchCamera} />
                </div>
              )}
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <div className="text-center">
                {permissionDenied ? (
                  <>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-red-500/20">
                      <CameraOff className="w-7 h-7 text-red-400" />
                    </div>
                    <p className="text-red-400 font-medium mb-1">Camera Permission Required</p>
                    <p className="text-gray-300 text-sm mb-4">
                      Allow camera access in your browser settings, then try again.
                    </p>
                  </>
                ) : connecting ? (
                  <>
                    <RefreshCw className="w-8 h-8 text-pink-300 animate-spin mx-auto mb-3" />
                    <p className="text-gray-300 text-sm">Connecting camera…</p>
                  </>
                ) : (
                  <>
                    <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4 bg-gradient-to-br from-pink-500/20 to-blue-500/20 border border-pink-500/20">
                      <Camera className="w-7 h-7 text-pink-300" />
                    </div>
                    <p className="text-gray-300 text-sm mb-1">{error || "Camera isn't connected yet."}</p>
                  </>
                )}
                {!connecting && (
                  <button
                    onClick={onRetryCamera}
                    style={TOUCH_FEEDBACK_STYLE}
                    className="mt-2 px-5 py-3 rounded-full text-sm font-medium text-white bg-gradient-to-r from-pink-500 to-blue-500 active:scale-95 transition-transform"
                  >
                    {permissionDenied ? "Try Again" : "Tap to enable camera"}
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {error && cameraArmed && (
        <div className="mx-5 mb-2 p-3 bg-red-900/30 border border-red-500/50 rounded-lg">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="flex gap-3 px-5 pt-3" style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}>
        {showBack && <WizardBackButton onClick={onBack} disabled={confirmLoading} />}
        <WizardPrimaryButton
          onClick={onConfirm}
          disabled={!cameraArmed || !isLandscape || confirmLoading}
          busy={confirmLoading}
        >
          {confirmLoading ? "Starting…" : "Start Monitoring"}
        </WizardPrimaryButton>
      </div>
    </div>
  );
};

export default PositioningSlide;
